import React, { useState } from 'react';
import { Launch } from '@core/icons-react';
import { DropdownContainer } from '../shared';

const cx = (...classes: (string | boolean | undefined)[]) => 
  classes.filter(Boolean).join(' ');

export interface ActionDropdownOption {
  id: string;
  label?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  hidden?: boolean;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

export interface ActionDropdownProps {
  isOpen: boolean;
  onClose?: () => void;
  viewOptions?: ActionDropdownOption[];  // Top section for viewing/navigating
  actionOptions?: ActionDropdownOption[];  // Bottom section for actions
  position?: 'left' | 'right';
  direction?: 'down' | 'up';
  size?: 'small' | 'medium';
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  anchorRef?: React.RefObject<HTMLElement>;
  className?: string;
  variant?: 'subtle' | 'default' | 'strong';
}

export function ActionDropdown({
  isOpen,
  onClose,
  viewOptions = [],
  actionOptions = [],
  position = 'left',
  direction = 'down',
  size = 'medium',
  minWidth = '200px',
  maxWidth = '20rem',
  maxHeight = '20rem',
  anchorRef,
  className,
  variant = 'default'
}: ActionDropdownProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const handleOptionClick = (option: ActionDropdownOption) => {
    if (option.disabled) return;
    option.onClick();
    onClose?.();
  };

  const renderOption = (option: ActionDropdownOption, isViewOption: boolean = false) => {
    const isHovered = hoveredItem === option.id;
    const showLaunchIcon = isViewOption && isHovered;
    
    return (
      <button
        key={option.id}
        className={cx(
          'core-action-dropdown__option',
          option.disabled && 'core-action-dropdown__option--disabled',
          option.variant === 'danger' && 'core-action-dropdown__option--danger',
          isHovered && 'core-action-dropdown__option--hovered'
        )}
        onClick={() => handleOptionClick(option)}
        onMouseEnter={() => setHoveredItem(option.id)}
        onMouseLeave={() => setHoveredItem(null)}
        disabled={option.disabled}
        aria-label={option.label || 'Action'}
      >
        {/* Text label */}
        {option.label && (
          <span className="core-action-dropdown__option-label">
            {option.disabled && option.disabledReason ? option.disabledReason : option.label}
          </span>
        )}
        
        {/* Right side icon logic */}
        {isViewOption ? (
          // View options: show launch icon on hover
          showLaunchIcon && (
            <Launch 
              size={16} 
              className="core-action-dropdown__option-launch"
            />
          )
        ) : (
          // Action options: always show icon if provided
          option.icon && (
            <span className="core-action-dropdown__option-icon-right">
              {option.icon}
            </span>
          )
        )}
      </button>
    );
  };

  const visibleViewOptions = viewOptions.filter(option => !option.hidden);
  const visibleActionOptions = actionOptions.filter(option => !option.hidden);
  const hasViewOptions = visibleViewOptions.length > 0;
  const hasActionOptions = visibleActionOptions.length > 0;
  const showDivider = hasViewOptions && hasActionOptions;

  return (
    <DropdownContainer
      isOpen={isOpen}
      onClose={onClose}
      anchorRef={anchorRef}
      position={position}
      direction={direction}
      variant={variant}
      size={size}
      minWidth={minWidth}
      maxWidth={maxWidth}
      className={cx('core-action-dropdown', className)}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ maxHeight, overflowY: 'auto' }}>
      {/* View Options Section */}
      {hasViewOptions && (
        <div className="core-action-dropdown__section core-action-dropdown__section--view">
          {visibleViewOptions.map(option => renderOption(option, true))}
        </div>
      )}
      
      {/* Divider */}
      {showDivider && (
        <div className="core-action-dropdown__divider" />
      )}
      
      {/* Action Options Section */}
      {hasActionOptions && (
        <div className="core-action-dropdown__section core-action-dropdown__section--actions">
          {visibleActionOptions.map(option => renderOption(option, false))}
        </div>
      )}
      </div>
    </DropdownContainer>
  );
}

export default ActionDropdown;
